import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { ArrowLeft, Plane, CheckCircle, ArrowRight, Play } from "lucide-react";
import { useLang } from "../LangContext";
import Breadcrumb from "../Breadcrumb";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/rb17en5i_logo_prodimmo-removebg-preview.png";

export default function DroneFpvImmobilier() {
  const { t } = useLang();
  const videoRef = useRef(null);
  const videoInView = useInView(videoRef, { once: false });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (videoInView) v.play().catch(() => {});
    else v.pause();
  }, [videoInView]);

  const features = [
    "Vol FPV à travers les pièces pour un effet immersif unique",
    "Survol aérien cinématique de la propriété et du terrain",
    "Passages fluides entre intérieur et extérieur en un seul plan",
    "Rendu comparable à un travelling de cinéma en 3D",
    "Résolution 4K avec stabilisation professionnelle",
    "Pilote certifié OFAC pour les vols en Suisse",
  ];

  const advantages = [
    {
      title: "Immersion totale",
      desc: "Le drone FPV plonge le spectateur au cœur du bien. Contrairement aux plans statiques, il crée une expérience de visite virtuelle fluide et captivante."
    },
    {
      title: "Impossible avec un drone classique",
      desc: "Grâce à sa taille réduite et sa maniabilité, le drone FPV vole à travers les fenêtres, les couloirs et les pièces , des plans impossibles autrement."
    },
    {
      title: "Un effet wow garanti",
      desc: "Les vidéos FPV immobilières génèrent un engagement massif sur les réseaux sociaux et font ressortir vos annonces de la concurrence."
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Drone FPV Immobilier , Vidéo Drone Cinématique | Prodimmo Suisse</title>
        <meta name="description" content="Vidéo drone FPV immobilière en Suisse romande. Prises de vue cinématiques immersives pour sublimer vos biens. Pilote certifié OFAC, résolution 4K." />
        <meta property="og:title" content="Drone FPV Immobilier , Vidéo Drone Cinématique | Prodimmo" />
        <meta property="og:description" content="Le drone FPV révolutionne la vidéo immobilière. Des plans spectaculaires impossibles avec un drone classique." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.prodimmo.ch/drone-fpv-immobilier" />
      </Helmet>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Prodimmo" className="h-9" />
            <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Prodimmo</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#73B5E2] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[
            { label: "Prestations", href: "/#prestations" },
            { label: "Drone FPV Immobilier" },
          ]} />
        </div>
      </nav>

      {/* Hero with video */}
      <section className="relative pt-24">
        <div className="relative h-[60vh] overflow-hidden">
          <video
            ref={videoRef}
            muted loop playsInline preload="auto"
            className="w-full h-full object-cover"
          >
            <source src="/about.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 via-[#0F172A]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">Drone FPV immobilier</span>
              <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Outfit' }}>
                Vidéo drone FPV pour<br />
                <span className="text-[#73B5E2]">l'immobilier en Suisse</span>
              </h1>
              <p className="mt-4 text-white/80 text-lg max-w-2xl">
                Le drone FPV révolutionne la vidéo immobilière. Des prises de vue cinématiques immersives 
                qui plongent les acheteurs au cœur de votre bien , un rendu spectaculaire impossible à reproduire autrement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
              Qu'est-ce que le drone FPV ?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Le drone FPV (First Person View) est un drone piloté en vue immersive grâce à des lunettes spéciales. 
              Contrairement aux drones classiques qui se déplacent lentement et de manière rigide, le FPV offre une 
              liberté de mouvement totale : accélérations, rotations, passages dans des espaces étroits.
            </p>
            <p className="text-slate-600 leading-relaxed">
              En immobilier, cela se traduit par des vidéos spectaculaires où le drone entre par la porte d'entrée, 
              traverse le salon, monte les escaliers, survole le jardin , le tout en un seul plan séquence fluide 
              qui donne l'impression d'une visite magique du bien.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
              Ce que comprend notre service FPV
            </h2>
            <ul className="space-y-4">
              {features.map((f, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#73B5E2] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{f}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-10 text-center" style={{ fontFamily: 'Outfit' }}>
            Pourquoi choisir le drone FPV pour votre bien
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {advantages.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.15 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl bg-[#73B5E2]/10 flex items-center justify-center mb-4">
                  <Plane className="w-5 h-5 text-[#73B5E2]" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{a.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vimeo video examples */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: 'Outfit' }}>
            Nos réalisations drone FPV
          </h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto">Découvrez la qualité de nos prises de vue FPV immobilières</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[1175439826, 1175940458].map((id, i) => (
              <motion.div key={id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.15 }} className="rounded-2xl overflow-hidden shadow-lg bg-slate-900 aspect-video">
                <iframe
                  src={`https://player.vimeo.com/video/${id}?autoplay=0&title=0&byline=0&portrait=0`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={`Vidéo drone FPV Prodimmo ${i + 1}`}
                  data-testid={`drone-vimeo-${i}`}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Prêt à sublimer votre bien avec le drone FPV ?
          </h2>
          <p className="text-slate-600 mb-8">Contactez-nous pour un devis gratuit et personnalisé.</p>
          <div className="flex justify-center gap-4">
            <Link to="/#contact" className="bg-[#73B5E2] text-white hover:bg-[#5AA0D0] rounded-full px-8 py-3 text-sm font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/20">
              Réserver un shooting FPV
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center gap-4">
          <Link to="/video-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Vidéo immobilière <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/photo-immobilier" className="flex items-center gap-2 text-[#73B5E2] hover:text-[#5AA0D0] font-medium text-sm transition-colors">
            Photo immobilière <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
